/**
 * Mini-cart developer upsell — which developer to offer, if any.
 *
 * A colour or bleach in the basket with no developer alongside it is the single
 * biggest measured gap in the order book: 70% of colour orders carry no developer,
 * and 44% of colour customers buy less than 80% of what their colour requires.
 *
 * The map is keyed by colour LINE, never by brand. Wella Koleston takes Welloxon;
 * Wella Color Touch takes the Color Touch emulsion. Same brand, different developer,
 * and handing a Color Touch customer a bottle of Welloxon is a mistake a colourist
 * spots immediately.
 *
 * Selection is pure and lives here rather than inline in the snippet so
 * tests/cart-developer-upsell.test.mjs can evaluate THIS file — the one the theme
 * serves. A mirror can be green while the shipping file is broken; this repo has
 * shipped a dead feature that way before.
 *
 * Pure library, copied verbatim from HDS shopify/assets/cart-developer-upsell.js (7d95c07).
 * On fanola.hair only assets/cart-dev-popout.js calls it. The HDS-only "others chain" code
 * stays (the Fanola map has no others_chain / own_brand / generic, so it never runs) to keep
 * the two copies diffable.
 */
(function () {
  'use strict';

  /**
   * @param cartItems  items from /cart.js — {handle, quantity, final_line_price}
   * @param map        developer-map.json — {pairs: {colourHandle: {...}}, developers: []}
   * @returns the one developer to offer, or null to stay quiet
   */
  function pick(cartItems, map) {
    var items = cartItems || [];
    var pairs = (map && map.pairs) || null;
    var developers = (map && map.developers) || [];
    if (!pairs || !items.length) return null;

    // Any developer already in the basket ends it. Which one they chose is their
    // business — second-guessing a colourist who has clearly bought developer
    // this order is how a helpful prompt becomes a nuisance.
    for (var d = 0; d < items.length; d++) {
      if (developers.indexOf(handleOf(items[d])) !== -1) return null;
    }

    // Koleston is ONE product with 151 shade variants, so a real basket is many
    // lines sharing a handle. Aggregate by handle or six shades read as six
    // colours, each with one tube.
    var totals = {};
    for (var i = 0; i < items.length; i++) {
      var handle = handleOf(items[i]);
      if (!handle || !pairs[handle]) continue;
      var bucket = totals[handle] || (totals[handle] = { tubes: 0, value: 0 });
      bucket.tubes += numberOf(items[i].quantity, 1);
      bucket.value += numberOf(items[i].final_line_price, 0);
    }

    var best = null;
    for (var key in totals) {
      if (!Object.prototype.hasOwnProperty.call(totals, key)) continue;
      if (best === null || beats(totals[key], totals[best])) best = key;
    }
    if (best === null) return null;

    var pair = pairs[best];
    return {
      colourHandle: best,
      colourName: pair.colour_name || '',
      developerHandle: pair.developer,
      // Null when the buying team's note states no ratio for this line. The card
      // then says nothing about mixing rather than inventing a number — silence
      // in a thin note is not evidence, and reading it as one has misled before.
      ratio: pair.ratio === undefined ? null : pair.ratio,
      label: pair.label || '',
      // 'tube' for colour, null for bleach - powder ships in bags, and "3 tubes of
      // Blondor Powder" is the kind of wrong that costs a prompt its authority.
      unit: pair.unit === undefined ? null : pair.unit,
      tubes: totals[best].tubes
    };
  }

  /** More tubes wins; a tie falls to the bigger line value. */
  function beats(candidate, incumbent) {
    if (candidate.tubes !== incumbent.tubes) return candidate.tubes > incumbent.tubes;
    return candidate.value > incumbent.value;
  }

  function handleOf(item) {
    return (item && typeof item.handle === 'string') ? item.handle : '';
  }

  function numberOf(value, fallback) {
    var n = typeof value === 'number' ? value : parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  /**
   * Chip labels for a developer hub's variants.
   *
   * The strength option is named "Product - Developer Strength" on most lines,
   * "Peroxide Strength" and "Strength" on others, so read the variant TITLE rather
   * than an option index that differs line to line.
   *
   * Five hubs carry a Size option too. Every one stocks a single size today, so
   * "6% (20vol) / 1 Litre" can safely show as "6% (20vol)". The day one stocks two
   * sizes of one strength, dropping the size would render two identical buttons
   * that add different products - so the size is only dropped when what remains is
   * still unique.
   */
  function strengthLabels(variants) {
    var titles = (variants || []).map(function (v) { return (v && v.title) || ''; });
    var short = titles.map(function (t) { return t.split(' / ')[0] || t; });
    var seen = {};
    for (var i = 0; i < short.length; i++) {
      if (seen[short[i]]) return titles;
      seen[short[i]] = true;
    }
    return short;
  }

  /** The one sentence the card leads with. Plain text; the caller escapes. */
  function prompt(choice) {
    var what = choice.colourName || 'your colour';
    if (!choice.ratio) {
      return what + ' needs developer \u2014 there is none in your basket.';
    }
    var n = choice.tubes;
    var count = choice.unit ? n + ' ' + choice.unit + (n === 1 ? '' : 's') : n + ' \u00d7';
    var joiner = choice.unit ? ' of ' : ' ';
    // The subject decides the verb: with a unit it is "3 tubes" (plural), without one
    // it is the product itself - "3 x Wella Blondor Powder mixes 1:1.5".
    var verb = (choice.unit && n !== 1) ? 'mix' : 'mixes';
    return count + joiner + what + ' ' + verb + ' ' + choice.ratio +
           ' \u2014 you will need developer.';
  }

  /** "6% (20vol) / 1 Litre" -> "6%". No percentage -> the text minus any trailing
      " / <size>" option segment, trimmed: "Tone On Tone Developer / 1 Litre" ->
      "Tone On Tone Developer" (a chip never carries the size). */
  function strengthKey(text) {
    var m = /(\d+(?:\.\d+)?)\s*%/.exec(text || '');
    if (m) return m[1] + '%';
    return String(text || '').replace(/\s+\/\s+.*$/, '').trim();
  }

  function pct(key) { var n = parseFloat(key); return isNaN(n) ? 999 : n; }

  /** The strength one variant sells. A single-strength product titles its only
      variant "Default Title", so its strength lives in the PRODUCT title. */
  function variantStrength(product, variant) {
    var t = variant && variant.title;
    if (!t || t === 'Default Title') return strengthKey(product && product.title);
    return strengthKey(t);
  }

  function familyRows(map, familyId, products, group) {
    var fam = map && map.families && map.families[familyId];
    if (!fam) return [];
    var out = [];
    (fam.sizes || []).forEach(function (size) {
      (size.handles || []).forEach(function (handle) {
        var p = products[handle];
        if (!p) return;
        (p.variants || []).forEach(function (v) {
          if (!v.available) return;
          out.push({ size: size.label, handle: handle, variantId: v.id, price: v.price,
                     strength: variantStrength(p, v), group: group, groupLabel: fam.label,
                     note: fam.note || '' });
        });
      });
    });
    return out;
  }

  function familyOf(map, handle) {
    var fams = (map && map.families) || {};
    for (var id in fams) {
      if (!Object.prototype.hasOwnProperty.call(fams, id)) continue;
      var sizes = fams[id].sizes || [];
      for (var s = 0; s < sizes.length; s++) {
        if ((sizes[s].handles || []).indexOf(handle) !== -1) return { id: id, fam: fams[id], size: sizes[s].label };
      }
    }
    return null;
  }

  /**
   * "Show other peroxide options" chain (owner, 2026-10-01): HDS own brand > Pro-Oxide >
   * 3deluxe > Fanola Color, each a single 1L PRODUCT. One array of in-stock rows per chain
   * entry, in order; a product belonging to the matched family itself is skipped.
   */
  function chainRows(map, familyId, products) {
    var own = map.families[familyId];
    var mine = {};
    (own.sizes || []).forEach(function (s) { (s.handles || []).forEach(function (h) { mine[h] = true; }); });
    return (map.others_chain || []).filter(function (h) { return !mine[h]; }).map(function (h) {
      var f = familyOf(map, h), p = products[h];
      if (!f || !p) return [];
      var group = f.id === map.own_brand ? 'own-brand' : 'other';
      return (p.variants || []).filter(function (v) { return v.available; }).map(function (v) {
        return { size: f.size, handle: h, variantId: v.id, price: v.price,
                 strength: variantStrength(p, v), group: group, groupLabel: f.fam.label,
                 note: f.fam.note || '' };
      });
    });
  }

  /** Every product handle an offer needs fetched: the matched family (all sizes) plus the chain. */
  function offerHandles(map, familyId, includeOthers) {
    var fam = map && map.families && map.families[familyId];
    if (!fam) return [];
    var hs = [];
    (fam.sizes || []).forEach(function (s) { hs = hs.concat(s.handles || []); });
    if (includeOthers && !fam.locked) {
      if (map.others_chain) hs = hs.concat(map.others_chain);
      else [map.own_brand].concat(map.generic || []).forEach(function (id) {
        var f = map.families[id];
        (f ? f.sizes : []).forEach(function (s) { hs = hs.concat(s.handles || []); });
      });
    }
    var seen = {};
    return hs.filter(function (h) { if (!h || seen[h]) return false; seen[h] = true; return true; });
  }

  /**
   * Everything the pop-out and the drawer card need for one developer family.
   * Pure: product.js payloads are passed in, nothing is fetched here.
   */
  function developerOffer(map, familyId, products, opts) {
    var fam = map && map.families && map.families[familyId];
    if (!fam) return null;
    opts = opts || {};
    products = products || {};
    var branded = familyRows(map, familyId, products, 'branded');
    var others = [];
    var chain = null;
    if (opts.includeOthers && !fam.locked && map.others_chain) {
      chain = chainRows(map, familyId, products);
    } else if (opts.includeOthers && !fam.locked) {
      if (map.own_brand && map.own_brand !== familyId) {
        others = others.concat(familyRows(map, map.own_brand, products, 'own-brand'));
      }
      (map.generic || []).forEach(function (g) {
        if (g !== familyId && g !== map.own_brand) others = others.concat(familyRows(map, g, products, 'other'));
      });
    }
    // Chips: what the matching brand stocks, PLUS any strength only a fallback stocks - so
    // a shopper whose brand is out of 6% can still pick 6% and be shown the fallback 6%.
    var mineSeen = {}, seen = {};
    branded.forEach(function (r) { mineSeen[r.strength] = true; seen[r.strength] = true; });
    // Fallbacks only add PERCENTAGE strengths: a 3deluxe "Tone on Tone" is not a strength a
    // Koleston shopper is choosing between.
    var addFallback = function (r) { if (/%$/.test(r.strength)) seen[r.strength] = true; };
    others.forEach(addFallback);
    (chain || []).forEach(function (rs) { rs.forEach(addFallback); });
    var strengths = Object.keys(seen).sort(function (a, b) { return pct(a) - pct(b); });
    var near6 = function (list) {
      return list.slice().sort(function (a, b) { return Math.abs(pct(a) - 6) - Math.abs(pct(b) - 6); })[0] || null;
    };
    // Default: the line's most co-bought strength if anyone stocks it; else the brand's own
    // strength nearest 6%; else the nearest anyone stocks.
    var def = opts.defaultStrength && seen[opts.defaultStrength] ? opts.defaultStrength : null;
    if (!def) def = near6(Object.keys(mineSeen)) || near6(strengths);
    return {
      familyId: familyId, label: fam.label, locked: !!fam.locked,
      strengths: strengths, defaultStrength: def,
      /** true when the matching brand itself stocks this strength */
      brandHas: function (strength) { return !!mineSeen[strength]; },
      rows: function (strength) {
        var mine = branded.concat(others).filter(function (r) { return r.strength === strength; });
        if (!chain) return mine;
        // Every chain product that stocks this strength, in the owner's order
        // (HDS > Pro-Oxide > 3deluxe > Fanola). The first is the fallback when the brand is out.
        chain.forEach(function (rs) {
          mine = mine.concat(rs.filter(function (r) { return r.strength === strength; }));
        });
        return mine;
      }
    };
  }

  window.CartDeveloperUpsell = { pick: pick, strengthLabels: strengthLabels, prompt: prompt,
                                 strengthKey: strengthKey, variantStrength: variantStrength,
                                 developerOffer: developerOffer, offerHandles: offerHandles };
})();
